import React, { useState } from 'react';
import { View } from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { router } from 'expo-router';

import { ThemedText } from '@/components/themed-text';
import { Card } from '@/components/ui/card';
import { PressableScale } from '@/components/ui/pressable-scale';
import { AppointmentCard } from './appointment-card';
import { AppointmentStatusPill } from './appointment-status-pill';
import { useSubscriptionStatus } from '@/hooks/use-subscriptions';
import { Appointment } from '@/types/appointment';
import { formatAppointmentWhen } from '@/utils/format-appointment';
import { formatQuotaBalance } from '@/utils/format-quota';

interface Props {
  enrollmentId: string;
  appointments: Appointment[];
  /** Abre o detalhe de um encontro — remarcar e cancelar ficam lá. */
  onOpen?: (a: Appointment) => void;
  /** Histórico não oferece marcar: o saldo do período só interessa adiante. */
  canBook?: boolean;
}

/** Cartão único para uma recorrência: mostra o próximo encontro recolhido e
 * expande para os demais, cada um abrindo o próprio detalhe. */
export function RecurrenceAppointmentGroup({
  enrollmentId,
  appointments,
  onOpen,
  canBook,
}: Props) {
  const [expanded, setExpanded] = useState(false);
  const [first, ...rest] = appointments;
  const extraCount = rest.length;

  // Assinatura: sobrou cota no período, então ainda dá para marcar mais uma
  // sessão dentro deste contrato — sem isso o cliente acha que o plano só
  // rende o que já está na grade.
  const { data: subscription } = useSubscriptionStatus(
    canBook ? first.serviceId : undefined,
  );
  const usage =
    subscription?.active && subscription.enrollmentId === enrollmentId
      ? subscription.usage
      : null;
  const hasFreeQuota = !!usage && usage.remaining > 0;

  return (
    <View className="gap-2">
      <Card className="w-full">
        <PressableScale onPress={() => setExpanded((v) => !v)}>
          <View className="gap-3 p-4">
            <View className="flex-row items-start justify-between gap-3">
              <View className="min-w-0 flex-1 gap-1">
                <View className="flex-row items-center gap-1.5">
                  <Ionicons name="repeat" size={13} color="#FD5001" />
                  <ThemedText className="text-xs font-semibold text-primary">
                    Recorrente
                  </ThemedText>
                </View>
                <ThemedText className="font-semibold text-base" numberOfLines={2}>
                  {first.serviceName ?? 'Serviço'}
                </ThemedText>
                <View className="flex-row items-center gap-1.5">
                  <Ionicons className="text-neutral-500" name="calendar-outline" size={14} />
                  <ThemedText className="text-sm text-neutral-500">
                    {formatAppointmentWhen(first.startAt)}
                  </ThemedText>
                </View>
                <ThemedText className="text-xs text-neutral-400">
                  {extraCount > 0
                    ? `+ ${extraCount} encontro${extraCount > 1 ? 's' : ''} agendado${extraCount > 1 ? 's' : ''}`
                    : 'Único encontro agendado'}
                </ThemedText>
              </View>
              <View className="items-end gap-2">
                <AppointmentStatusPill status={first.status} />
                {extraCount > 0 && (
                  <Ionicons className="text-neutral-400"
                    name={expanded ? 'chevron-up' : 'chevron-down'}
                    size={16}
                  />
                )}
              </View>
            </View>
          </View>
        </PressableScale>

        {hasFreeQuota && (
          <PressableScale
            onPress={() =>
              router.push({
                pathname: '/(tabs)/(client)/service/[serviceId]',
                params: { serviceId: first.serviceId },
              })
            }
          >
            <View className="flex-row items-center gap-2 border-t border-neutral-100 px-4 py-3">
              <Ionicons name="add-circle-outline" size={16} color="#FD5001" />
              <ThemedText className="flex-1 text-sm font-semibold text-primary">
                {formatQuotaBalance(usage!.remaining, usage!.quota, usage!.period)}
                {' · marcar'}
              </ThemedText>
              <Ionicons name="chevron-forward" size={16} color="#FD5001" />
            </View>
          </PressableScale>
        )}
      </Card>

      {expanded && (
        <View className="gap-2 pl-3">
          {appointments.map((a) => (
            <AppointmentCard key={a.id} appointment={a} onOpen={onOpen} />
          ))}
        </View>
      )}
    </View>
  );
}
